/**
 * BottomSheet — slide-up sheet from bottom. Used on mobile/tablet viewports
 * as a native-feeling alternative to side panels. Supports swipe-down dismiss,
 * Escape key, backdrop click, and safe-area insets.
 * Spec: docs/responsive-redesign.md § Technical notes — BottomSheet
 */
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { X as XIcon } from 'lucide-react'
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type Transition,
} from 'motion/react'
import { useViewportHeight } from '../../hooks/use-viewport-height'
import { cn } from '../../ui/cn'

export type BottomSheetSize = 'auto' | 'full' | 'fraction'

export type BottomSheetProps = {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  /** Controls sheet height. 'auto' = content-height; 'full' = full viewport;
   *  'fraction' = 60% of viewport. Default: 'auto'. */
  size?: BottomSheetSize
}

/** Drag-down distance (px) required to dismiss the sheet. */
const DISMISS_THRESHOLD_PX = 100

export function BottomSheet({
  open,
  onClose,
  title,
  children,
  size = 'auto',
}: BottomSheetProps) {
  const titleId = useId()
  const sheetRef = useRef<HTMLDivElement>(null)
  const prefersReducedMotion = useReducedMotion()
  const vvh = useViewportHeight()

  // ── Drag-to-dismiss ──────────────────────────────────────────────────────
  const dragStartY = useRef<number | null>(null)
  const [dragOffset, setDragOffset] = useState(0)
  const isDragging = useRef(false)

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Only start drag from the handle area (the first 40px of the sheet).
    // Skip drag if the pointer landed on an interactive element (button, a, input).
    const target = e.target as HTMLElement
    if (target.closest('button, a, input, select, textarea')) return
    const rect = sheetRef.current?.getBoundingClientRect()
    if (!rect) return
    if (e.clientY - rect.top > 40) return
    dragStartY.current = e.clientY
    isDragging.current = true
    // setPointerCapture may be unavailable in jsdom test environments
    ;(e.currentTarget as HTMLDivElement).setPointerCapture?.(e.pointerId)
  }

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current || dragStartY.current === null) return
    const delta = e.clientY - dragStartY.current
    if (delta > 0) setDragOffset(delta)
  }

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current || dragStartY.current === null) return
    isDragging.current = false
    const delta = e.clientY - dragStartY.current
    dragStartY.current = null

    const sheetH = sheetRef.current?.offsetHeight ?? 0
    const threshold = Math.min(DISMISS_THRESHOLD_PX, sheetH * 0.4)
    if (delta >= threshold) {
      onClose()
    }
    setDragOffset(0)
  }

  // ── Escape key ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  // ── Body scroll lock ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  // ── Focus first focusable element on open ─────────────────────────────────
  useEffect(() => {
    if (!open) return
    const el = sheetRef.current
    if (!el) return
    const target = el.querySelector<HTMLElement>(
      '[data-autofocus], button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    )
    target?.focus()
  }, [open])

  // ── Sheet height from size prop ───────────────────────────────────────────
  const maxHeight =
    size === 'full'
      ? `${vvh - 16}px`
      : size === 'fraction'
        ? `${Math.round(vvh * 0.6)}px`
        : `calc(${vvh}px - 16px)`

  const backdropClose = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose()
    },
    [onClose],
  )

  const transition: Transition = prefersReducedMotion
    ? { duration: 0 }
    : { type: 'spring', damping: 28, stiffness: 300 }

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            className="bottom-sheet-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.2 }}
            onClick={backdropClose}
            aria-hidden="true"
          />

          {/* Sheet */}
          <motion.div
            ref={sheetRef}
            className={cn('bottom-sheet', `bottom-sheet--${size}`)}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? titleId : undefined}
            style={{
              maxHeight,
              transform: `translateY(${dragOffset}px)`,
            }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={transition}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            tabIndex={-1}
          >
            {/* Drag handle */}
            <div className="bottom-sheet-handle" aria-hidden="true" />

            {/* Header */}
            {title !== undefined && (
              <div className="bottom-sheet-head">
                <h2 id={titleId}>{title}</h2>
                <button
                  type="button"
                  className="tap-target btn ghost sm"
                  onClick={onClose}
                  aria-label="Cerrar"
                >
                  <XIcon size={16} />
                </button>
              </div>
            )}

            {/* Body — opt-in to native selection so users can copy text
                (phone, email, notes, factura number) from detail sheets. */}
            <div className="bottom-sheet-body selectable">{children}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
